import { useQueryClient } from '@tanstack/react-query';
import { Form, Formik, FormikHelpers } from 'formik';
import moment from 'moment';
import { useMemo } from 'react';
import { TbSquareRoundedPlusFilled } from 'react-icons/tb';
import { toast } from 'react-toastify';
import { SELECT_FIELD_DEFAULT_VALUE } from '../../../constants/settings';
import { useAuthContext } from '../../../contexts/AuthProvider';
import useManualTransactionActions from '../../../hooks/APIHooks/finance/useManualTransactionActions';
import usePackageActions from '../../../hooks/APIHooks/finance/usePackageActions';
import usePaymentMethodActions from '../../../hooks/APIHooks/finance/usePaymentMethodActions';
import useClientActions from '../../../hooks/APIHooks/users/useClientActions';
import { createEditTransactionSchema } from '../../../schemas';
import { SelectData } from '../../../types/common';
import { ManualTransactionHistory } from '../../../types/history';
import { showFullName } from '../../../utils/commonFunctions';
import { fetchErrorMessage } from '../../../utils/fetchErrorMessage';
import AcademiaButton from '../Buttons/AcademiaButton';
import AcademiaDatePicker from '../Inputs/AcademiaDatePicker';
import AcademiaFileInputField from '../Inputs/AcademiaFileInputField';
import AcademiaFormSelect from '../Inputs/AcademiaFormSelect';
import AcademiaInputField from '../Inputs/AcademiaInputField';
import ModalTitle from './ModalTitle';
import ParentModal from './ParentModal';

const CreateEditTransactionModal = ({
  title,
  manualTransaction,
  handleModalClose,
}: {
  title: 'create' | 'update';
  manualTransaction?: ManualTransactionHistory;
  handleModalClose: () => void;
}) => {
  // integration of react hooks here
  // const [packageList, setPackageList] = useState<Array<SelectData>>([]);
  //const [paymentMethodList, setPaymentMethodList] = useState<Array<SelectData>>(
  //  []
  // );

  // integration of custom API hooks here
  const { clientListHandler } = useClientActions('clientListHandler');
  const { getPackageListHandler } = usePackageActions('packageList');
  const { getPaymentMethodListHandler } = usePaymentMethodActions();
  const { createManualTransactionHandler, updateManualTransactionHandler } =
    useManualTransactionActions('');

  // integration of context hooks here
  const { currentUser } = useAuthContext();

  // integration of tanstack query hooks here
  const queryClient = useQueryClient();

  // formatting credit packages here
  // useEffect(() => {
  //   if (getPackageListHandler.data?.is_success) {
  //     setPackageList(
  //       getPackageListHandler.data.data.map((pointPackage) => ({
  //         id: pointPackage.id,
  //         name: pointPackage.name,
  //         code: `${pointPackage.credits} Credits @${pointPackage.price} CAD`,
  //       }))
  //     );
  //   }
  // }, [getPackageListHandler.data]);

  // formatting payment methods here
  // useEffect(() => {
  //   if (getPaymentMethodListHandler.data?.is_success) {
  //     setPaymentMethodList(
  //       getPaymentMethodListHandler.data.data.map((paymentMethod, index) => ({
  //         id: index,
  //         name: paymentMethod.payment_method,
  //       }))
  //     );
  //   }
  // }, [getPaymentMethodListHandler.data]);

  const clientOptions = useMemo(() => {
    if (!clientListHandler.data?.data) return [];

    return clientListHandler.data.data.map((client) => ({
      id: client.id,
      name: showFullName(
        client.first_name,
        client.middle_name,
        client.last_name
      ),
    }));
  }, [clientListHandler.data]);

  const paymentOptions = useMemo(() => {
    if (!getPaymentMethodListHandler.data?.is_success) return [];

    return getPaymentMethodListHandler.data.data.map(
      (paymentMethod, index) => ({
        id: index,
        name: paymentMethod.payment_method,
      })
    );
  }, [getPaymentMethodListHandler.data]);

  const packageOptions = useMemo(() => {
    if (!getPackageListHandler.data?.is_success) return [];

    return getPackageListHandler.data.data.map((pointPackage) => ({
      id: pointPackage.id,
      name: pointPackage.name,
      code: `${pointPackage.credits} Credits @${pointPackage.price} CAD`,
    }));
  }, [getPackageListHandler.data]);

  // handler function to create/update manual transaction declared here
  const createUpdateManualTransactionHandler = async (
    values: any,
    actions: FormikHelpers<any>
  ) => {
    try {
      actions.setSubmitting(true);

      console.log(values);

      // selecting package details from package details here
      const selectedPackage = getPackageListHandler.data?.data.find(
        (creditPackage) => creditPackage.id === values.credit_package.id
      );

      const body = new FormData();

      body.append('client', values.client.id);
      body.append('transaction_id', values.transaction_id);
      body.append(
        'transaction_date',
        moment(values.transaction_date, 'DD-MM-YYYY').format('YYYY-MM-DD')
      );
      body.append('payment_method', values.payment_method.name);
      body.append('cost_amount', values.paid_amount);
      body.append(
        'credits_purchased',
        (selectedPackage?.credits as number).toString()
      );
      body.append('point_package', values.credit_package.id);
      body.append('receipt', values.receipt);

      if (!manualTransaction) {
        body.append('created_by', currentUser?.username as string);

        await createManualTransactionHandler
          .mutateAsync({ body })
          .then((res) => {
            if (res.is_success) {
              toast.success('Transaction Created!!');

              handleModalClose();
              queryClient.invalidateQueries({
                queryKey: ['manual-transaction-list'],
              });
            } else {
              toast.error(res.message || 'Transaction Creation Failed!!');
            }
          })
          .catch((error) => toast.error(fetchErrorMessage(error)));
      } else {
        body.append('updated_by', currentUser?.username as string);

        await updateManualTransactionHandler
          .mutateAsync({ tranId: manualTransaction.id, body })
          .then((res) => {
            if (res.is_success) {
              toast.success('Transaction Updated!!');

              handleModalClose();
              queryClient.invalidateQueries({
                queryKey: ['manual-transaction-list'],
              });
            } else {
              toast.error(res.message || 'Transaction Updating Failed!!');
            }
          })
          .catch((error) => toast.error(fetchErrorMessage(error)));
      }
    } catch (error) {
      toast.error('Something Went Wrong!!');
    } finally {
      actions.setSubmitting(false);
    }
  };

  // rendering create transaction modal component here
  return (
    <>
      {packageOptions.length && paymentOptions.length ? (
        <ParentModal onClick={handleModalClose}>
          <ModalTitle titleText={`${title} transaction`} />

          <Formik
            initialValues={{
              client: manualTransaction?.client
                ? {
                    id: manualTransaction.client,
                    name: manualTransaction.client_full_name,
                  }
                : SELECT_FIELD_DEFAULT_VALUE,
              payment_method: manualTransaction?.payment_method
                ? (paymentOptions.find(
                    (paymentMethod) =>
                      paymentMethod.name === manualTransaction.payment_method
                  ) as SelectData)
                : SELECT_FIELD_DEFAULT_VALUE,
              transaction_id: manualTransaction?.transaction_id || '',
              transaction_date: manualTransaction?.transaction_date || '',
              paid_amount: manualTransaction?.cost_amount || '',
              credit_package: manualTransaction?.point_package
                ? (packageOptions.find(
                    (pointPackage) =>
                      pointPackage.id === manualTransaction.point_package
                  ) as SelectData)
                : SELECT_FIELD_DEFAULT_VALUE,
              receipt: '',
            }}
            validationSchema={createEditTransactionSchema}
            onSubmit={createUpdateManualTransactionHandler}
          >
            {(props) => (
              <Form className='space-y-5'>
                <AcademiaFormSelect
                  name='client'
                  label='Select Client'
                  options={clientOptions}
                  defaultValue={props.values.client}
                  showSearchInput
                  isRequired
                />

                <AcademiaFormSelect
                  name='payment_method'
                  label='Select Payment Method'
                  options={paymentOptions}
                  defaultValue={props.values.payment_method}
                  showSearchInput
                  isRequired
                />

                {props.values.payment_method.name !== 'Cash' ? (
                  <AcademiaInputField
                    type='text'
                    name='transaction_id'
                    label='Transaction ID'
                    placeholder={`Enter client's transaction id here...`}
                    isRequired
                  />
                ) : null}

                <AcademiaDatePicker
                  name='transaction_date'
                  label='Transaction Date'
                  placeholder='DD-MM-YYYY'
                  maxDate={new Date()}
                  defaultDate={
                    manualTransaction?.transaction_date
                      ? new Date(manualTransaction.transaction_date)
                      : new Date()
                  }
                  isRequired
                />

                <AcademiaInputField
                  type='number'
                  name='paid_amount'
                  label='Paid Amount'
                  placeholder='Enter paid amount here in BDT...'
                  isRequired
                />

                <AcademiaFormSelect
                  name='credit_package'
                  label='Select Package'
                  options={packageOptions}
                  optionDescription={packageOptions.map(
                    (pointPackage) => pointPackage.code as string
                  )}
                  defaultValue={props.values.credit_package}
                  showSearchInput
                  isRequired
                />

                <AcademiaFileInputField
                  name='receipt'
                  label='Receipt Image'
                  btnText='Upload'
                  accept='images/*'
                  placeholder='Upload Receipt Image Here...'
                  isRequired
                  filePath={
                    manualTransaction?.receipt
                      ? `${process.env.REACT_APP_FINANCE_SERVICE_PROD_API}/${manualTransaction.receipt}`
                      : ''
                  }
                />

                <div className='!mt-6'>
                  <AcademiaButton
                    title={
                      props.values.client.id === -1
                        ? 'Please, select a client first!'
                        : props.values.credit_package.id === -1
                        ? 'Please, select a credit package first!'
                        : props.values.payment_method.id === -1
                        ? 'Please, select a payment method first!'
                        : undefined
                    }
                    type='submit'
                    variant='premium'
                    icon={<TbSquareRoundedPlusFilled className='size-6' />}
                    isLoading={props.isSubmitting}
                    isDisabled={
                      props.values.client.id === -1 ||
                      props.values.credit_package.id === -1 ||
                      props.values.payment_method.id === -1
                    }
                    fullWidth
                  >
                    {title} transaction
                  </AcademiaButton>
                </div>
              </Form>
            )}
          </Formik>
        </ParentModal>
      ) : null}
    </>
  );
};

export default CreateEditTransactionModal;
